import React, { useMemo, useRef, useEffect } from "react";
import * as THREE from "three";
import { Edges, Html } from "@react-three/drei";
import type { ElementCoordinateSystem } from "../common_types";
import { computeOrientation } from "../transformations";
import { useFrame, useThree } from "@react-three/fiber";
import { useStoreSelector } from "../../../../../../../../hooks/StoreHooks";
import { ConditionalAxesHelper, LatoPergola, sezioneTrave } from "../Pergola";
import { InstallationType } from "../../forms/Pergola_type";
import coloriJson from '../../../../assets/teli.json';


interface DumpstarProps {
  lunghezza: number;
  material: THREE.Texture;
  cs: ElementCoordinateSystem;
  needExtra: boolean;
}
interface TendaProps {
  lunghezza: number;
  material: THREE.Texture;
  cs: ElementCoordinateSystem;
  isOpen?: boolean;
  altezza: number;
  rotation?: [number, number, number];
  isCristal?: boolean;
}

type CompleteTendaProps = {
  lunghezzaSoletta: number;
  larghezzaSoletta: number;
  altezzaPilastro: number;
  sezionePilastro: Sezione;
  sezioneTrave: Sezione;
  texture: any;
  textureTelo: any;
  statoTenda: boolean;
  lato: LatoPergola;
  needExtra: boolean;
  isCristal?: boolean;
  muro: number;
  segmentiLiberi: { start: number; end: number }[];
  nstacchi: number;
};

type Sezione = { larghezza: number; altezza: number };

const sezioneDumpstar: Sezione = { larghezza: 0.125, altezza: 0.145 };
const sezioneTeminale: Sezione = { larghezza: 0.034, altezza: 0.034 };
const sezioneGuidaZip: Sezione = { larghezza: 0.05, altezza: 0.065 };
const sezioneTenda: Sezione = { larghezza: 0.0065, altezza: 0 };

const codiciDaMostrare = ["50265"];

const tableData = coloriJson
  .filter(item => codiciDaMostrare.includes(item.code))
  .map(item => ({
    colore: item.code,
    ts: item.ts,
    rs: item.rs,
    as: item.as,
    tv: item.tv,
  }));  

/**
 * Telo con animazione apertura/chiusura e supporto cristal
 */
export const Tela: React.FC<TendaProps> = ({
  lunghezza,
  material,
  cs,
  isOpen = true,
  altezza,
  rotation: rotationInput,
  isCristal = false,
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const infoDotRef = useRef<THREE.Mesh>(null);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [showTable, setShowTable] = React.useState(false);
  const currentScale = useRef(isOpen ? 1 : 0);
  const targetScale = useRef(isOpen ? 1 : 0);
  const { invalidate } = useThree();

  const baseRotation = useMemo(() => computeOrientation(cs.direction), [cs.direction]);

  const finalRotation: [number, number, number] = useMemo(() => {
    if (!rotationInput) return baseRotation as [number, number, number];
    return [
      baseRotation[0] + rotationInput[0],
      baseRotation[1] + rotationInput[1],
      baseRotation[2] + rotationInput[2],
    ];
  }, [baseRotation, rotationInput]);

  useEffect(() => {
    if (isAnimating) return;
    const newTarget = isOpen ? 1 : 0;
    if (Math.abs(currentScale.current - newTarget) > 0.01) {
      targetScale.current = newTarget;
      setIsAnimating(true);
    }
  }, [isOpen]);

  useFrame((_, delta) => {
    if (isAnimating && meshRef.current) {
      currentScale.current = THREE.MathUtils.lerp(currentScale.current, targetScale.current, 2 * delta);
      meshRef.current.scale.z = currentScale.current;
      meshRef.current.position.z = (altezza * (1 - currentScale.current)) / 2;

      if (Math.abs(currentScale.current - targetScale.current) < 0.01) {
        currentScale.current = targetScale.current;
        setIsAnimating(false);
      }
      invalidate();
    }
  });

  const handleInfoDotClick = () => setShowTable(!showTable);
  const handleCloseTable = () => setShowTable(false);

  return (
    <group position={[cs.org.x - 0.02, cs.org.y, cs.org.z - sezioneGuidaZip.altezza / 2]} rotation={finalRotation}>
      <mesh ref={meshRef}>
        <boxGeometry args={[sezioneTenda.larghezza, lunghezza, altezza]} />
        {isCristal ? (
          <meshPhysicalMaterial
            side={THREE.DoubleSide}
            color={0xffffff}
            transmission={0.9}
            roughness={0.1}
            ior={1.5}
            thickness={0.01}
            transparent={true}
            envMapIntensity={1.0}
            clearcoat={0.5}
            clearcoatRoughness={0.1}
            opacity={0.95}
            depthWrite={false}
            polygonOffset={true}
            polygonOffsetFactor={-2}
            polygonOffsetUnits={-2}
            alphaTest={0.1}
          />
        ) : (
          <meshPhysicalMaterial side={THREE.DoubleSide} map={material} />
        )}
        <Edges color="#363636" />
        <ConditionalAxesHelper />
      </mesh>

      <mesh ref={infoDotRef} position={[0, lunghezza / 2 + 0.3, altezza / 2]} onClick={handleInfoDotClick}>
        <sphereGeometry args={[0.05, 16, 16]} />
        <meshStandardMaterial color="yellow" />
      </mesh>

      {showTable && (
        <Html position={[0, sezioneTenda.altezza / 2 + 0.2, lunghezza / 2]} center zIndexRange={[100, 0]}>
          <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200 max-w-md relative">
            <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700" onClick={handleCloseTable}>
              X
            </button>
            <div className="w-full text-sm">
              <h1 className="text-center text-xl font-bold mb-2">Telo</h1>
              <table className="table-auto w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1">Colore</th>
                    <th className="px-2 py-1">Ts %</th>
                    <th className="px-2 py-1">Rs %</th>
                    <th className="px-2 py-1">As %</th>
                    <th className="px-2 py-1">Tv %</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-2 py-1">{row.colore}</td>
                      <td className="px-2 py-1">{row.ts}</td>
                      <td className="px-2 py-1">{row.rs}</td>
                      <td className="px-2 py-1">{row.as}</td>
                      <td className="px-2 py-1">{row.tv}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Html>
      )}
    </group>
  );
};

/**
 * Cassonetto
 */
export const Cassonetto: React.FC<DumpstarProps> = ({ lunghezza, material, cs, needExtra }) => {
  const rotation = useMemo(() => computeOrientation(cs.direction), [cs.direction]);
  return (
    <mesh
      position={[
        cs.org.x - 0.030,
        cs.org.y,
        cs.org.z - sezioneDumpstar.altezza / 2 + 0.02,
      ]}
      rotation={[rotation[0], rotation[1], rotation[2]]}
    >
      <boxGeometry args={[sezioneDumpstar.larghezza, sezioneDumpstar.altezza, lunghezza]} />
      <meshStandardMaterial map={material} />
      <ConditionalAxesHelper />
    </mesh>
  );
};

/**
 * Guida ZIP
 */
export const GuidaZip: React.FC<DumpstarProps> = ({ lunghezza, material, cs, needExtra }) => {
  const rotation = useMemo(() => computeOrientation(cs.direction), [cs.direction]);
  return (
    <mesh position={[cs.org.x - 0.020, cs.org.y, cs.org.z - sezioneGuidaZip.altezza / 2]} rotation={[rotation[0], rotation[1], rotation[2]]}>
      <boxGeometry args={[sezioneGuidaZip.larghezza, sezioneGuidaZip.altezza, lunghezza - (needExtra ? sezioneTrave.altezza : 0)]} />
      <meshStandardMaterial map={material} />
      <Edges color="#363636" />
      <ConditionalAxesHelper />
    </mesh>
  );
};

/**
 * Terminale inferiore
 */
export const Terminale: React.FC<DumpstarProps> = ({ lunghezza, material, cs }) => {
  const rotation = useMemo(() => computeOrientation(cs.direction), [cs.direction]);
  return (
    <mesh position={[cs.org.x - 0.020, cs.org.y, cs.org.z + sezioneTeminale.altezza / 2]} rotation={[rotation[0], rotation[1], rotation[2]]}>
      <boxGeometry args={[sezioneTeminale.larghezza, sezioneTeminale.altezza, lunghezza - sezioneGuidaZip.larghezza * 2]} />
      <meshStandardMaterial map={material} />
      <Edges color="#363636" />
      <ConditionalAxesHelper />
    </mesh>
  );
};
const getSegmentoParams = (
  direzione: string,
  start: number,
  end: number,
  lato: LatoPergola,
  installazione: InstallationType | undefined,
  altezzaPilastro: number,
  larghezzaSoletta: number,
  lunghezzaSoletta: number,
  sezionePilastro: Sezione,
  sezioneTrave: Sezione,
  sezioneGuidaZip: Sezione,
  needExtra: boolean,
  muro: number,
  nstacchi: number
) => {
  const centro = (start + end) / 2;
  const offsetExtra = needExtra ? sezioneTrave.altezza - 0.01 : 0;
  let offsetGuida1 = 0;
  if(lato.segmento <= 1){
    offsetGuida1 = sezioneGuidaZip.larghezza + sezionePilastro.larghezza/2 + 0.05;
  }else{
    offsetGuida1 = sezioneGuidaZip.larghezza + sezionePilastro.larghezza/2 ;
  }
  let offsetGuida2 =  0;
  if(lato.segmento <= 1){
    offsetGuida2 = sezionePilastro.larghezza/2;
  }else if(lato.segmento === nstacchi){
    offsetGuida2 = sezionePilastro.larghezza/2 + sezionePilastro.larghezza - 0.05;
  }else{
    offsetGuida2 = sezionePilastro.larghezza/6 + sezionePilastro.larghezza - 0.05*2;
  }
  let offsetGuida2Fronte =  0;
  if(lato.segmento <= 1){
    offsetGuida2Fronte = sezionePilastro.larghezza/2;
  }else if(lato.segmento === nstacchi){
    offsetGuida2Fronte = sezionePilastro.larghezza/2 ;
  }else{
    offsetGuida2Fronte = sezionePilastro.larghezza/2 + sezionePilastro.larghezza - 0.06*2 - 0.02;
  }
  let offsetGuida1Fronte =  0;
  if(lato.segmento <= 1){
    offsetGuida1Fronte = sezioneGuidaZip.larghezza;
  }else if(lato.segmento === nstacchi){
    offsetGuida1Fronte = sezionePilastro.larghezza/6 - 0.06;
  }else{
    offsetGuida1Fronte = sezionePilastro.larghezza/2 + sezionePilastro.larghezza/2 - 0.06*2 - 0.04;
  }
  
  if(lato.direzione === 'Fronte' && lato.segmento === 1 && installazione === InstallationType.addossata_profondita){
    offsetGuida1Fronte -= sezionePilastro.larghezza
  }
  let offsetCassonetto = 0;
  if(lato.segmento <= 1){
    offsetCassonetto = sezioneGuidaZip.larghezza;
  }else if(lato.segmento === nstacchi){
    offsetCassonetto = 0 - 0.02;
  }else{
    offsetCassonetto = 0 - 0.02;
  }

  switch (direzione) {
    case "Destra":
      return {
        cassonetto: { x: larghezzaSoletta - sezioneTrave.larghezza / 2, y: centro , z: altezzaPilastro - offsetExtra },
        guidaZip1: { x: larghezzaSoletta - sezioneTrave.larghezza / 2, y: start + offsetGuida1, z: altezzaPilastro / 2 },
        guidaZip2: { x: larghezzaSoletta - sezioneTrave.larghezza / 2, y: end - offsetGuida2, z: altezzaPilastro / 2},
        terminale: { x: larghezzaSoletta - sezioneTrave.larghezza / 2, y: centro, z: muro },
        tela: { x: larghezzaSoletta - sezioneTrave.larghezza / 2, y: centro, z: altezzaPilastro / 2 + muro / 2 },
        rotation: [0, 0, 0] as [number, number, number],
        direction: [0, 1, 0] as [number, number, number],
      };

    case "Sinistra":
      return {
        cassonetto: { x: sezioneTrave.larghezza + 0.01 , y: centro + offsetCassonetto , z: altezzaPilastro - offsetExtra },
        guidaZip1: { x: sezioneTrave.larghezza - sezioneGuidaZip.larghezza / 2, y: start + offsetGuida1, z: altezzaPilastro / 2},
        guidaZip2: { x: sezioneTrave.larghezza - sezioneGuidaZip.larghezza / 2, y: end - offsetGuida2, z: altezzaPilastro / 2},
        terminale: { x: sezioneTrave.larghezza / 2, y: centro , z: muro },
        tela: { x: sezioneTrave.larghezza / 2, y: centro, z: altezzaPilastro / 2 + muro / 2 },
        rotation: [0, 0, 0] as [number, number, number],
        direction: [0, 1, 0] as [number, number, number],
      };
    case "Fronte":
      return {
        cassonetto: { x: centro - 0.02, y: sezioneTrave.larghezza / 2 + 0.02, z: altezzaPilastro - offsetExtra },
        guidaZip1: { x: start + offsetGuida1Fronte, y: sezioneTrave.larghezza - sezioneGuidaZip.larghezza / 2, z: altezzaPilastro / 2 },
        guidaZip2: { x: end - offsetGuida2Fronte, y: sezioneTrave.larghezza - sezioneGuidaZip.larghezza / 2, z: altezzaPilastro / 2 },
        terminale: { x: centro+ sezioneGuidaZip.larghezza, y: sezioneTrave.larghezza / 2 + 0.02, z: muro },
        tela: { x: centro , y: sezioneTrave.larghezza / 2 + 0.02, z: altezzaPilastro / 2 + muro / 2 },
        rotation: [0, 0, Math.PI / 2] as [number, number, number],
        direction: [1, 0, 0] as [number, number, number],
      };

    case "Retro":
      return {
        cassonetto: { x: centro, y: lunghezzaSoletta - sezioneTrave.larghezza / 2 - 0.02, z: altezzaPilastro - offsetExtra },
        guidaZip1: { x: start + offsetGuida1Fronte, y: lunghezzaSoletta - sezioneTrave.larghezza + sezioneGuidaZip.larghezza / 2, z: altezzaPilastro / 2 },
        guidaZip2: { x: end - offsetGuida2Fronte, y: lunghezzaSoletta - sezioneTrave.larghezza + sezioneGuidaZip.larghezza / 2, z: altezzaPilastro / 2 },
        terminale: { x: centro, y: lunghezzaSoletta - sezioneTrave.larghezza / 2 - 0.02, z: muro },
        tela: { x: centro, y: lunghezzaSoletta - sezioneTrave.larghezza / 2 - 0.02, z: altezzaPilastro / 2 + muro / 2 },
        rotation: [0, 0, Math.PI / 2] as [number, number, number],
        direction: [1, 0, 0] as [number, number, number],
      };

    default:
      return {
        cassonetto: { x: 0, y: 0, z: 0 },
        guidaZip1: { x: 0, y: 0, z: 0 },
        guidaZip2: { x: 0, y: 0, z: 0 },
        terminale: { x: 0, y: 0, z: 0 },
        tela: { x: 0, y: 0, z: 0 },
        rotation: [0, 0, 0] as [number, number, number],
        direction: [0, 0, 0] as [number, number, number],
      };
  }
};

export const CompleteTenda: React.FC<CompleteTendaProps> = ({
  lunghezzaSoletta,
  larghezzaSoletta,
  altezzaPilastro,
  sezionePilastro,
  sezioneTrave,
  texture,
  textureTelo,
  statoTenda,
  lato,
  needExtra,
  isCristal = false,
  muro,
  segmentiLiberi,
  nstacchi
}) => {
  const pergolaConfiguration = useStoreSelector((store) => store.pergolaConfigurationBuilder);
  const installazione = pergolaConfiguration.configuration?.dimensioni.installazione;
  return (
    <>
      {segmentiLiberi.map((seg, idx) => {
        let lunghezzaSegmento = seg.end - seg.start ;
        if(lato.segmento <= 1){
          lunghezzaSegmento = seg.end - seg.start - sezionePilastro.larghezza;
        }else if (lato.segmento === nstacchi && (lato.direzione === 'Destra' || lato.direzione === 'Sinistra')){
          lunghezzaSegmento = seg.end - seg.start - sezionePilastro.larghezza*2;
        }
        let diff = 0;
        let diffEnd = 0;
        if(lato.segmento !== 1){
          diff = 0.04;
          diffEnd = -0.135;
        }
        if(lato.segmento === 1 && lato.direzione === 'Fronte'){
          diff = sezionePilastro.larghezza;
        }
        if(lato.direzione === "Sinistra" || lato.direzione === "Destra" && (lato.segmento === 1 ||lato.segmento === nstacchi)){
          lunghezzaSegmento += 0.135;
        }
        if((lato.direzione === 'Fronte' || lato.direzione === 'Retro') && lato.segmento === 1 && installazione === InstallationType.addossata_profondita){
          lunghezzaSegmento += 0.135;
          diff = 0.03;
        }
        if((lato.direzione === 'Sinistra' || lato.direzione === 'Destra') && lato.segmento === nstacchi && installazione === InstallationType.addossata_larghezza){
          lunghezzaSegmento += 0.135;
          diffEnd = -0.03;
        }
        if((lato.direzione === "Sinistra" || lato.direzione === "Destra") && lato.segmento === nstacchi && installazione === InstallationType.patio){
          lunghezzaSegmento += 0.135;
          diffEnd = -0.03;
        }
        const p = getSegmentoParams(
          lato.direzione,
          seg.start + diff,
          seg.end + diffEnd,
          lato,
          installazione,
          altezzaPilastro,
          larghezzaSoletta,
          lunghezzaSoletta,
          sezionePilastro,
          sezioneTrave,
          sezioneGuidaZip,
          needExtra,
          muro,
          nstacchi
        );

        return (
          <group key={`${lato.direzione}-tenda-segmento-${idx}`}>
            <Cassonetto lunghezza={lunghezzaSegmento} material={texture} cs={{ org: p.cassonetto, direction: p.direction }} needExtra={needExtra} />
            <Terminale lunghezza={lunghezzaSegmento + 0.02} material={texture} cs={{ org: p.terminale, direction: p.direction }} needExtra={needExtra}  />
            <Tela
              lunghezza={lunghezzaSegmento - 0.03}
              material={textureTelo}
              cs={{ org: p.tela, direction: [0, 0, 0] }}
              rotation={p.rotation}
              isOpen={!statoTenda}
              altezza={altezzaPilastro - muro}
              isCristal={isCristal}
            />
          </group>
        );
      })}
    </>
  );
};